import type { PopupMenuFunction } from "@/features/app/PopupMenuProvider";
import { DrawMode } from "@/features/geojson_editor/modes/DrawMode";
import { EmptyMode } from "@/features/geojson_editor/modes/EmptyMode";
import { BaseMode, type ChangeCallback } from "./BaseMode";
import { BatchSelectMode } from "./BatchSelectMode";
import { DirectSelectMode } from "./DirectSelectMode";

export enum EditorModeType {
  DIRECT = "direct",
  BATCH = "batch",
  DRAW = "draw",
}

export interface ModeConstructor {
  new (
    onChange: ChangeCallback,
    openPopupMenu: PopupMenuFunction,
    changeCursor: (cursor: string) => void,
  ): BaseMode;
}

export const MODE_REGISTRY: Record<EditorModeType, ModeConstructor> = {
  [EditorModeType.DIRECT]: DirectSelectMode,
  [EditorModeType.BATCH]: BatchSelectMode,
  [EditorModeType.DRAW]: DrawMode,
};

export function createMode(
  modeType: EditorModeType | null,
  onChange: ChangeCallback,
  openPopupMenu: PopupMenuFunction,
  changeCursor: (cursor: string) => void,
): BaseMode {
  if (modeType == null) {
    return new EmptyMode(onChange, openPopupMenu, changeCursor);
  }
  const ModeConstructor = MODE_REGISTRY[modeType];
  if (!ModeConstructor) {
    throw new Error(`Unknown editor mode: ${modeType}`);
  }
  return new ModeConstructor(onChange, openPopupMenu, changeCursor);
}

export function isValidMode(modeType: string): modeType is EditorModeType {
  return modeType in MODE_REGISTRY;
}

export function getAvailableModes(): EditorModeType[] {
  return Object.keys(MODE_REGISTRY) as EditorModeType[];
}

export { BaseMode, BatchSelectMode, DirectSelectMode, DrawMode };
